import {
  SHIELDED_POOL_JOINSPLIT_V2_INTERFACE,
  buildFlatJoinSplitV2Transaction,
  buildFlatMigrateV2Transaction,
  buildFlatPrivateBridgeV2Transaction,
  buildFlatPrivateSwapV2Transaction,
  buildFlatWithdrawBatchV2Transaction,
  buildFlatWithdrawV2Transaction,
  validateFlatJoinSplitV2WithdrawBatchEntries,
  computeFlatJoinSplitV2BridgeAuxHash,
  computeFlatJoinSplitV2SwapAuxHash,
  proveFlatJoinSplitV2,
  readFlatJoinSplitV2PoolSnapshot,
  resolveFlatJoinSplitV2Action,
  selectFlatJoinSplitV2Arity,
  simulateFlatJoinSplitV2Transaction,
  type FlatJoinSplitV2BridgeData,
  type FlatJoinSplitV2Hex32,
  type FlatJoinSplitV2HexData,
  type FlatJoinSplitV2InputNoteWitness,
  type FlatJoinSplitV2OutputNoteWitness,
  type FlatJoinSplitV2ProveRequest,
  type FlatJoinSplitV2SwapData,
} from "./sdk";
import { EVM_CONFIG } from "../config";
import {
  submitBasePoolRelay,
  serializeTransaction,
} from "../utils/relayer-client";
import { computeNPK, computeNullifier } from "../joinsplit/keys";
import type {
  JoinSplitNote,
  SpendingKeys,
  StoredJoinSplitNote,
} from "../joinsplit/types";
import {
  scanFlatJoinSplitV2Tree,
  type FlatJoinSplitV2MerklePath,
} from "./merkleIndex";
import {
  assertFlatJoinSplitV2Note,
  createFlatJoinSplitV2Note,
  flatFieldHex,
} from "./notes";
import { withFlatJoinSplitV2Prover } from "./prover";
import { assertAscendingLeafIndices, selectForAmount } from "./spendSelection";
import {
  markFlatJoinSplitV2SettlementBroadcast,
  markFlatJoinSplitV2SettlementFinalized,
  markFlatJoinSplitV2SettlementIncluded,
  prepareFlatJoinSplitV2SettlementJournal,
  type FlatJoinSplitV2JournalDelivery,
  type FlatJoinSplitV2JournalKind,
  type FlatJoinSplitV2JournalScope,
} from "./pendingJournal";
import { fail } from "./common";

// Dependency-free so pricing code can select candidates without the prover.
export { candidateNotes } from "./noteCandidates";

export async function authenticateAndDedupeCandidates(params: {
  candidates: StoredJoinSplitNote[];
  pool: string;
  chainId: bigint;
  tokenAddress: string;
  spendingKeys: SpendingKeys;
}): Promise<StoredJoinSplitNote[]> {
  const authenticated = new Map<string, StoredJoinSplitNote>();
  for (const stored of params.candidates) {
    try {
      await assertFlatJoinSplitV2Note({
        note: stored.note,
        chainId: params.chainId,
        poolAddress: params.pool,
        tokenAddress: params.tokenAddress,
        expectedTokenHash: flatFieldHex(stored.note.tokenHash),
        expectedMpk: params.spendingKeys.mpk,
      });
      const key = stored.note.commitment.toLowerCase();
      const prior = authenticated.get(key);
      if (!prior || (prior.note.leafIndex < 0 && stored.note.leafIndex >= 0)) {
        authenticated.set(key, stored);
      }
    } catch {
      // Untrusted mailbox duplicates must not shadow an authentic plaintext
      // record for the same on-chain commitment.
    }
  }
  return Array.from(authenticated.values());
}

export function selectExact(
  notes: StoredJoinSplitNote[],
  amount: bigint,
): StoredJoinSplitNote[] | null {
  const exact = notes.find((note) => note.note.value === amount);
  if (exact) return [exact];
  const bounded = notes.slice(0, 32);
  const midpoint = Math.floor(bounded.length / 2);
  const left = bounded.slice(0, midpoint);
  const right = bounded.slice(midpoint);
  const enumerate = (items: StoredJoinSplitNote[]) => {
    const sums = new Map<bigint, number[]>();
    const count = 1 << items.length;
    for (let mask = 0; mask < count; mask += 1) {
      let sum = 0n;
      const indices: number[] = [];
      for (let index = 0; index < items.length; index += 1) {
        if ((mask & (1 << index)) !== 0) {
          sum += items[index].note.value;
          if (sum > amount) break;
          indices.push(index);
        }
      }
      if (sum <= amount && !sums.has(sum)) sums.set(sum, indices);
    }
    return sums;
  };
  const leftSums = enumerate(left);
  const rightSums = enumerate(right);
  for (const [leftSum, leftIndices] of leftSums) {
    const rightIndices = rightSums.get(amount - leftSum);
    if (!rightIndices) continue;
    const selected = [
      ...leftIndices.map((index) => left[index]),
      ...rightIndices.map((index) => right[index]),
    ];
    if (selected.length > 0) return selected;
  }
  return null;
}
